import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getCompanyProfile } from './companyProfile';
import { getFormPdfSettings } from './formPdfSettings';
import { linkToHome, pdfBackLink } from './pdfLinks';
import { formatDateTime, todayLocalISO } from './dates';

/**
 * Branded PDF of a report result ({ title, columns, rows }).
 *
 * Sam flagged the old output as "basic and needs proper work". It ignored the
 * company's own PDF settings entirely — no logo, no accent colour, no header or
 * footer — so a report looked nothing like the form PDFs sitting next to it. This
 * uses the same branding (Form settings → Forms PDF layout) so everything a client receives
 * looks like it came from the same firm.
 *
 * Layout deliberately kept conservative pending Harvey's input on house style:
 * a titled header block with an accent rule, a summary line, a zebra-striped
 * table with repeating headers, and a footer with page numbers.
 */
const M = 40;

const rgb = (hex) => {
  const h = String(hex || '').replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(h)) return [26, 128, 229];
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Give wide tables sensible widths instead of letting one column eat the page. */
const columnStyles = (cols) => {
  const out = {};
  cols.forEach((c, i) => {
    const label = String(c || '').toLowerCase();
    if (/date|due|expiry|created|updated/.test(label)) out[i] = { cellWidth: 68, halign: 'left' };
    else if (/status|priority|risk|result/.test(label)) out[i] = { cellWidth: 66 };
    else if (/^(no|id|ref|qty|count|total|value|cost|sale)$/.test(label)) out[i] = { cellWidth: 58, halign: 'right' };
  });
  return out;
};

export function downloadReportPdf(title, columns, rows, opts = {}) {
  const cols = Array.isArray(columns) ? columns : [];
  const data = Array.isArray(rows) ? rows : [];
  const landscape = cols.length > 6;
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: landscape ? 'landscape' : 'portrait' });

  const profile = getCompanyProfile();
  const settings = getFormPdfSettings();
  const accent = rgb(settings.accent);
  const company = String(profile?.name || '').trim();
  const companyLine = [company, profile?.companyCode ? `CloudSafe ID ${profile.companyCode}` : ''].filter(Boolean).join(' · ');
  const w = doc.internal.pageSize.getWidth();

  // ── Header block ──
  doc.setFontSize(9); doc.setFont(undefined, 'normal'); doc.setTextColor(120, 120, 120);
  if (companyLine) doc.text(companyLine, M, 34);

  if (settings.includeLogo && profile?.logo) {
    try { doc.addImage(profile.logo, /^data:image\/jpe?g/i.test(String(profile.logo)) ? 'JPEG' : 'PNG', w - M - 90, 20, 90, 34, undefined, 'FAST'); } catch { /* unsupported format */ }
  }

  // Accent tick + title, matching the form PDFs.
  doc.setFillColor(accent[0], accent[1], accent[2]);
  doc.rect(M, 48, 4, 15, 'F');
  doc.setFontSize(16); doc.setFont(undefined, 'bold'); doc.setTextColor(17, 24, 39);
  doc.text(String(title || 'Report'), M + 12, 61);

  let y = 78;
  if (settings.headerText?.trim()) {
    doc.setFontSize(9); doc.setFont(undefined, 'normal'); doc.setTextColor(120, 120, 120);
    doc.text(settings.headerText.trim(), M, y); y += 13;
  }

  // Summary line — what this is, and when it was true.
  doc.setFontSize(9); doc.setFont(undefined, 'normal'); doc.setTextColor(107, 114, 128);
  const summary = [
    `${data.length} row${data.length === 1 ? '' : 's'}`,
    opts.subtitle,
    `generated ${formatDateTime(new Date())}`,
  ].filter(Boolean).join('  ·  ');
  doc.text(summary, M, y); y += 8;

  doc.setDrawColor(accent[0], accent[1], accent[2]); doc.setLineWidth(1.5);
  doc.line(M, y, w - M, y);
  y += 14;

  autoTable(doc, {
    startY: y,
    head: [cols],
    body: data.length ? data : [[{ content: 'No rows matched this report.', colSpan: Math.max(1, cols.length), styles: { halign: 'center', textColor: [150, 150, 150], fontStyle: 'italic' } }]],
    styles: { fontSize: 8.5, cellPadding: 6, lineColor: [229, 231, 235], lineWidth: 0.5, overflow: 'linebreak', valign: 'middle' },
    headStyles: { fillColor: accent, textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
    alternateRowStyles: { fillColor: [249, 250, 251] },      // zebra — long tables get unreadable without it
    columnStyles: columnStyles(cols),
    margin: { left: M, right: M },
    rowPageBreak: 'avoid',
    showHead: 'everyPage',                                    // a page of headerless rows is meaningless
  });

  // ── Footer on every page ──
  const pages = doc.getNumberOfPages();
  const h = doc.internal.pageSize.getHeight();
  const footer = (settings.footerText || '').trim() || [company, 'CloudSafe'].filter(Boolean).join(' · ');
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(235, 238, 242); doc.setLineWidth(0.5);
    doc.line(M, h - 32, w - M, h - 32);
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(160, 160, 160);
    doc.text(footer, M, h - 18);
    doc.text(`Page ${p} of ${pages}`, w - M, h - 18, { align: 'right' });
    // Joe's point: every PDF is a marketing surface — link it back to the live data.
    pdfBackLink(doc, opts.backLink || linkToHome(), M, h - 44, opts.backLabel || 'View this live in CloudSafe');
  }

  doc.save(`${String(title || 'report').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${todayLocalISO()}.pdf`);
}

export default downloadReportPdf;
